import { createSlice } from '@reduxjs/toolkit';
import { getWidgetAction } from '../actions';
import {
  getWidgetPinsAction,
  getWidgetListAction,
  getMomentDetailAction,
  getWidgetLocalitiesAction,
  getWidgetDataAction,
} from '../actions/WidgetActions';

interface WidgetState {
  getWidgetData: any;
  getWidgetDataLoading: boolean;
  widgetDataPreview: any;
  widgetDataPreviewLoading: boolean;
  pins: { byWidgetId: Record<string, { data: any; loading: boolean }> };
  listPages: { byWidgetId: Record<string, { byCursor: Record<string, { data: any; nextCursor: string | null; loading: boolean }> }> };
  momentDetail: { byId: Record<string, { data: any; loading: boolean }> };
  localities: { byBusinessId: Record<string, { data: string[]; loading: boolean }> };
}

const initialState: WidgetState = {
  getWidgetData: null,
  getWidgetDataLoading: true,
  widgetDataPreview: null,
  widgetDataPreviewLoading: false,
  pins: { byWidgetId: {} },
  listPages: { byWidgetId: {} },
  momentDetail: { byId: {} },
  localities: { byBusinessId: {} }
};

const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // get Widget
      .addCase(getWidgetAction.pending, state => {
        state.getWidgetDataLoading = true;
      })
      .addCase(getWidgetAction.fulfilled, (state, action) => {
        state.getWidgetDataLoading = false;
        state.getWidgetData = action.payload.data;
      })
      .addCase(getWidgetAction.rejected, (state, action) => {
        state.getWidgetDataLoading = false;
      });

    builder

      // get Widget Pins (Tier 1)
      .addCase(getWidgetPinsAction.pending, (state, action) => {
        const widget_id = action.meta.arg.widget_id;
        state.pins.byWidgetId[widget_id] = { data: state.pins.byWidgetId[widget_id]?.data ?? null, loading: true };
      })
      .addCase(getWidgetPinsAction.fulfilled, (state, action) => {
        const widget_id = action.meta.arg.widget_id;
        state.pins.byWidgetId[widget_id] = { data: action.payload.data, loading: false };
      })
      .addCase(getWidgetPinsAction.rejected, (state, action) => {
        const widget_id = action.meta.arg.widget_id;
        state.pins.byWidgetId[widget_id] = { data: state.pins.byWidgetId[widget_id]?.data ?? null, loading: false };
      });

    builder

      // get Widget List (Tier 2)
      .addCase(getWidgetListAction.pending, (state, action) => {
        const { widget_id, cursor } = action.meta.arg;
        const key = cursor ?? 'first';
        if (!state.listPages.byWidgetId[widget_id] || !cursor) {
          state.listPages.byWidgetId[widget_id] = { byCursor: {} };
        }
        state.listPages.byWidgetId[widget_id].byCursor[key] = {
          data: state.listPages.byWidgetId[widget_id].byCursor[key]?.data ?? null,
          nextCursor: state.listPages.byWidgetId[widget_id].byCursor[key]?.nextCursor ?? null,
          loading: true
        };
      })
      .addCase(getWidgetListAction.fulfilled, (state, action) => {
        const { widget_id, cursor } = action.meta.arg;
        const key = cursor ?? 'first';
        if (!state.listPages.byWidgetId[widget_id]) {
          state.listPages.byWidgetId[widget_id] = { byCursor: {} };
        }
        state.listPages.byWidgetId[widget_id].byCursor[key] = { data: action.payload.data, nextCursor: action.payload.nextCursor ?? null, loading: false };
      })
      .addCase(getWidgetListAction.rejected, (state, action) => {
        const { widget_id, cursor } = action.meta.arg;
        const key = cursor ?? 'first';
        if (!state.listPages.byWidgetId[widget_id]) {
          state.listPages.byWidgetId[widget_id] = { byCursor: {} };
        }
        state.listPages.byWidgetId[widget_id].byCursor[key] = {
          data: state.listPages.byWidgetId[widget_id].byCursor[key]?.data ?? null,
          nextCursor: state.listPages.byWidgetId[widget_id].byCursor[key]?.nextCursor ?? null,
          loading: false
        };
      });

    builder

      // get Widget Localities
      .addCase(getWidgetLocalitiesAction.pending, (state, action) => {
        const business_id = action.meta.arg.business_id;
        state.localities.byBusinessId[business_id] = { data: state.localities.byBusinessId[business_id]?.data ?? [], loading: true };
      })
      .addCase(getWidgetLocalitiesAction.fulfilled, (state, action) => {
        const business_id = action.meta.arg.business_id;
        state.localities.byBusinessId[business_id] = { data: action.payload.data, loading: false };
      })
      .addCase(getWidgetLocalitiesAction.rejected, (state, action) => {
        const business_id = action.meta.arg.business_id;
        state.localities.byBusinessId[business_id] = { data: state.localities.byBusinessId[business_id]?.data ?? [], loading: false };
      });

    builder

      // get Moment Detail (Tier 3)
      .addCase(getMomentDetailAction.pending, (state, action) => {
        const post_id = action.meta.arg.post_id;
        state.momentDetail.byId[post_id] = { data: state.momentDetail.byId[post_id]?.data ?? null, loading: true };
      })
      .addCase(getMomentDetailAction.fulfilled, (state, action) => {
        const post_id = action.meta.arg.post_id;
        state.momentDetail.byId[post_id] = { data: action.payload.data, loading: false };
      })
      .addCase(getMomentDetailAction.rejected, (state, action) => {
        const post_id = action.meta.arg.post_id;
        state.momentDetail.byId[post_id] = { data: state.momentDetail.byId[post_id]?.data ?? null, loading: false };
      });

    builder

      // get Widget Data (Editor preview payload)
      .addCase(getWidgetDataAction.pending, state => {
        state.widgetDataPreviewLoading = true;
      })
      .addCase(getWidgetDataAction.fulfilled, (state, action) => {
        state.widgetDataPreviewLoading = false;
        state.widgetDataPreview = action.payload.data;
      })
      .addCase(getWidgetDataAction.rejected, state => {
        state.widgetDataPreviewLoading = false;
      });
  }
});

export default widgetSlice.reducer;
